import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com';
const ENV = import.meta.env.MODE;

let sentryReady = false;
let posthogReady = false;

export function initObservability() {
  // Sentry
  if (SENTRY_DSN) {
    try {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: ENV,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: ENV === 'production' ? 0.2 : 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
      sentryReady = true;
    } catch (e) {
      console.warn('[obs] Sentry init failed:', e);
    }
  }

  // PostHog
  if (POSTHOG_KEY) {
    try {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        session_recording: { maskAllInputs: true },
      });
      posthogReady = true;
    } catch (e) {
      console.warn('[obs] PostHog init failed:', e);
    }
  }

  // Dev feedback
  if (ENV !== 'production') {
    if (sentryReady || posthogReady) {
      console.info('[obs] Observability active — Sentry:', sentryReady, '| PostHog:', posthogReady);
    } else {
      console.info('[obs] Observability no-op (VITE_SENTRY_DSN / VITE_POSTHOG_KEY not set)');
    }
  }

  // Web Vitals (always-on, sent to PostHog if available)
  const reportVital = (metric) => {
    if (posthogReady) {
      posthog.capture('web_vital', {
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    }
  };
  onCLS(reportVital);
  onINP(reportVital);
  onLCP(reportVital);
  onFCP(reportVital);
  onTTFB(reportVital);
}

export function identifyUser(userId, traits = {}) {
  if (sentryReady) Sentry.setUser({ id: userId, ...traits });
  if (posthogReady) posthog.identify(userId, traits);
}

export function trackEvent(name, props = {}) {
  if (posthogReady) posthog.capture(name, props);
}

export function captureError(err, context = {}) {
  if (sentryReady) {
    Sentry.captureException(err, { extra: context });
  } else {
    console.error('[obs]', err, context);
  }
}

export function resetUser() {
  if (sentryReady) Sentry.setUser(null);
  if (posthogReady) posthog.reset();
}

export const isObservabilityEnabled = () => sentryReady || posthogReady;
